import type { SessionProviderPlatform } from "./use-session-provider-platform";
import type { SessionProviderStateBundle } from "./use-session-provider-state-bundle";
import type { SessionProviderLiveWiring } from "./use-session-provider-live-wiring";
import type { SessionProviderOverviewWiring } from "./use-session-provider-overview-wiring";
import type { SessionProviderRuntimeWiring } from "./use-session-provider-runtime-wiring";
import { useSessionChatActions } from "./use-session-chat-actions";
import { useSessionProductActions } from "./use-session-product-actions";
import { useSessionRefreshActions } from "./use-session-refresh-actions";

interface SessionProviderActionWiringInput {
  platform: SessionProviderPlatform;
  sessionId: string;
  state: SessionProviderStateBundle;
  liveWiring: SessionProviderLiveWiring;
  overviewWiring: SessionProviderOverviewWiring;
  runtimeWiring: SessionProviderRuntimeWiring;
}

interface SessionProviderChatActionWiringInput
  extends SessionProviderActionWiringInput {
  productActions: ReturnType<typeof useSessionProductActions>;
}

export interface SessionProviderActionWiring {
  productActions: ReturnType<typeof useSessionProductActions>;
  chatActions: ReturnType<typeof useSessionChatActions>;
  refreshActions: ReturnType<typeof useSessionRefreshActions>;
}

export function useSessionProviderActionWiring(
  input: SessionProviderActionWiringInput,
): SessionProviderActionWiring {
  const productActions = useSessionProviderProductActionWiring(input);
  const chatActions = useSessionProviderChatActionWiring({
    ...input,
    productActions,
  });
  const refreshActions = useSessionProviderRefreshActionWiring(input);

  return {
    productActions,
    chatActions,
    refreshActions,
  };
}

function useSessionProviderProductActionWiring(
  input: SessionProviderActionWiringInput,
): ReturnType<typeof useSessionProductActions> {
  const focusedProductId = input.runtimeWiring.navigation.navigation.focusedProductId;
  const focusedProductUpdatedAt = focusedProductId
    ? input.state.entity.products[focusedProductId]?.updatedAt
    : undefined;

  return useSessionProductActions({
    api: input.platform.api,
    sessionId: input.sessionId,
    products: input.state.entity.products,
    versions: input.state.entity.versions,
    productsSeen: input.liveWiring.readTracking.productsSeen,
    focusedProductId,
    focusedProductUpdatedAt,
    rootProductIdRef: input.state.refs.rootProductIdRef,
    collabs: input.state.conversation.collabs,
    collabsByProduct: input.state.conversation.collabsByProduct,
    setLoading: input.state.meta.setLoading,
    setProducts: input.state.entity.setProducts,
    setVersions: input.state.entity.setVersions,
    replaceProductCollabItems:
      input.overviewWiring.collabMerges.replaceProductCollabItems,
    tagOwnCollabsAndAdvanceRead:
      input.liveWiring.readTracking.tagOwnCollabsAndAdvanceRead,
    updateLocalProductReadMarker:
      input.liveWiring.readTracking.updateLocalProductReadMarker,
    replaceProductsSeen: input.liveWiring.readTracking.replaceProductsSeen,
    updateProductsSinceTimestamp:
      input.liveWiring.timestamps.updateProductsSinceTimestamp,
    updateProductsSeen: input.liveWiring.readTracking.updateProductsSeen,
  });
}

function useSessionProviderChatActionWiring(
  input: SessionProviderChatActionWiringInput,
): ReturnType<typeof useSessionChatActions> {
  return useSessionChatActions({
    api: input.platform.api,
    sessionId: input.sessionId,
    chat: input.state.interaction.chat,
    chatCursor: input.state.conversation.chatCursor,
    chatHasMore: input.state.conversation.chatHasMore,
    chatLoadingMore: input.state.conversation.chatLoadingMore,
    collabs: input.state.conversation.collabs,
    collabsByProduct: input.state.conversation.collabsByProduct,
    chatMessageIdsRef: input.state.refs.chatMessageIdsRef,
    ownMessageIdsRef: input.liveWiring.readTracking.ownMessageIdsRef,
    setChat: input.state.interaction.setChat,
    setChatMessages: input.state.conversation.setChatMessages,
    setChatMessageIds: input.state.conversation.setChatMessageIds,
    setChatCursor: input.state.conversation.setChatCursor,
    setChatHasMore: input.state.conversation.setChatHasMore,
    setChatLoadingMore: input.state.conversation.setChatLoadingMore,
    setCollabs: input.state.conversation.setCollabs,
    setCollabsByProduct: input.state.conversation.setCollabsByProduct,
    setNewMessageIds: input.state.interaction.setNewMessageIds,
    replaceProductCollabItems:
      input.overviewWiring.collabMerges.replaceProductCollabItems,
    tagOwnCollabsAndAdvanceRead:
      input.liveWiring.readTracking.tagOwnCollabsAndAdvanceRead,
    updateLocalProductReadMarker:
      input.liveWiring.readTracking.updateLocalProductReadMarker,
    updateProductsSeen: input.liveWiring.readTracking.updateProductsSeen,
    markAllChatAsRead: input.liveWiring.readTracking.markAllChatAsRead,
    markAllCollabsAsRead: input.liveWiring.readTracking.markAllCollabsAsRead,
    updateProductCollabsSinceTimestamp:
      input.liveWiring.timestamps.updateProductCollabsSinceTimestamp,
    pushMemberActivitiesFromCollabs:
      input.liveWiring.liveSignals.pushMemberActivitiesFromCollabs,
    refreshProducts: input.productActions.refreshProducts,
  });
}

function useSessionProviderRefreshActionWiring(
  input: SessionProviderActionWiringInput,
): ReturnType<typeof useSessionRefreshActions> {
  return useSessionRefreshActions({
    api: input.platform.api,
    router: input.platform.router,
    sessionId: input.sessionId,
    setLoading: input.state.meta.setLoading,
    setMembers: input.state.entity.setMembers,
    updateActivitySinceTimestamp:
      input.liveWiring.timestamps.updateActivitySinceTimestamp,
    applySessionFromSource: input.overviewWiring.overview.applySessionFromSource,
  });
}
